import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  Mic2,
  Search,
  Loader2,
  CheckCircle2,
  XCircle,
  Globe,
  Star,
  Play,
  FileAudio,
} from "lucide-react";

import adminService from "@/services/adminService";

export default function AdminNarrators() {
  const [loading, setLoading] = useState(true);
  const [narrators, setNarrators] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterLanguage, setFilterLanguage] = useState("all");
  const [filterAvailability, setFilterAvailability] = useState("all");
  const [selectedNarrator, setSelectedNarrator] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [narratorDemos, setNarratorDemos] = useState([]);
  const [demosLoading, setDemosLoading] = useState(false);
  const [reviewingDemoId, setReviewingDemoId] = useState(null);
  const [demoReviewNotes, setDemoReviewNotes] = useState({});
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadNarrators();
  }, []);

  useEffect(() => {
    if (showDrawer && selectedNarrator?.id) {
      loadNarratorDemos(selectedNarrator.id);
    } else {
      setNarratorDemos([]);
      setDemoReviewNotes({});
    }
  }, [showDrawer, selectedNarrator?.id]);

  const loadNarrators = async () => {
    try {
      setLoading(true);
      const data = await adminService.getNarrators();
      setNarrators(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading narrators:", error);
      toast.error("Failed to load narrators");
    } finally {
      setLoading(false);
    }
  };

  const loadNarratorDemos = async (narratorId) => {
    try {
      setDemosLoading(true);
      const data = await adminService.getNarratorDemos(narratorId);
      setNarratorDemos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading narrator demos:", error);
      toast.error("Failed to load narrator demos");
    } finally {
      setDemosLoading(false);
    }
  };

  const handleVerify = async (narratorId) => {
    try {
      setProcessing(true);
      await adminService.verifyNarrator(narratorId);
      toast.success("Narrator verified");
      await loadNarrators();
    } catch (error) {
      console.error("Error verifying:", error);
      toast.error("Failed to verify");
    } finally {
      setProcessing(false);
    }
  };

  const handleReviewDemo = async (demo, status) => {
    try {
      setReviewingDemoId(demo.id);
      await adminService.reviewDemo(demo.id, {
        status,
        is_public: status === "APPROVED",
        reviewer_notes: demoReviewNotes[demo.id]?.trim() || null,
      });

      toast.success(status === "APPROVED" ? "Demo approved" : "Demo rejected");
      await loadNarratorDemos(selectedNarrator.id);
      const updatedNarrators = await adminService.getNarrators();
      const nextNarrators = Array.isArray(updatedNarrators) ? updatedNarrators : [];
      setNarrators(nextNarrators);
      const updatedSelected = nextNarrators.find((n) => n.id === selectedNarrator.id);
      if (updatedSelected) setSelectedNarrator(updatedSelected);
    } catch (error) {
      console.error("Error reviewing demo:", error);
      toast.error(error.response?.data?.detail || "Failed to review demo");
    } finally {
      setReviewingDemoId(null);
    }
  };

  const getDemoStatusClass = (status) => {
    if (status === "APPROVED") return "bg-green-500/20 text-green-300";
    if (status === "REJECTED") return "bg-red-500/20 text-red-300";
    if (status === "SUBMITTED") return "bg-blue-500/20 text-blue-300";
    return "bg-slate-500/20 text-slate-300";
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return null;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (value) => {
    if (!value) return null;
    return new Date(value).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const filteredNarrators = narrators.filter((n) => {
    const matchesSearch =
      n.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      n.bio?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLanguage =
      filterLanguage === "all" || n.languages?.includes(filterLanguage);
    const matchesAvailability =
      filterAvailability === "all" || n.availability_status === filterAvailability;
    return matchesSearch && matchesLanguage && matchesAvailability;
  });

  // Get unique languages from all narrators
  const allLanguages = [...new Set(narrators.flatMap((n) => n.languages || []))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-narrators">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Narrators</h1>
          <p className="text-slate-400">Manage narrator directory and approvals</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search narrators..."
            className="pl-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <Select value={filterLanguage} onValueChange={setFilterLanguage}>
          <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
            <SelectValue placeholder="Language" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700 text-white">
            <SelectItem value="all">All Languages</SelectItem>
            {allLanguages.map((lang) => (
              <SelectItem key={lang} value={lang}>
                {lang}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterAvailability} onValueChange={setFilterAvailability}>
          <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
            <SelectValue placeholder="Availability" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700 text-white">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="AVAILABLE">Available</SelectItem>
            <SelectItem value="BUSY">Busy</SelectItem>
            <SelectItem value="ON_BREAK">On Break</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Narrators Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredNarrators.map((narrator) => (
          <Card
            key={narrator.id}
            className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-purple-500/50 transition-all"
            onClick={() => {
              setSelectedNarrator(narrator);
              setShowDrawer(true);
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                  <Mic2 className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-white truncate">
                      {narrator.display_name}
                    </h3>
                    {narrator.is_verified && (
                      <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge
                      className={
                        narrator.availability_status === "AVAILABLE"
                          ? "bg-green-500/20 text-green-300"
                          : narrator.availability_status === "BUSY"
                          ? "bg-yellow-500/20 text-yellow-300"
                          : "bg-slate-500/20 text-slate-300"
                      }
                    >
                      {narrator.availability_status}
                    </Badge>
                    <Badge
                      className={
                        narrator.portfolio_status === "APPROVED"
                          ? "bg-blue-500/20 text-blue-300"
                          : "bg-slate-500/20 text-slate-300"
                      }
                    >
                      {narrator.portfolio_status}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-4 text-sm text-slate-400">
                <div className="flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  <span>{narrator.languages?.length || 0} langs</span>
                </div>
                <div className="flex items-center gap-1">
                  <Play className="w-3 h-3" />
                  <span>{narrator.demos_count || 0} demos</span>
                </div>
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  <span>{narrator.projects_count || 0} projects</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredNarrators.length === 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-12 text-center text-slate-500">
            <Mic2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No narrators found</p>
          </CardContent>
        </Card>
      )}

      {/* Narrator Drawer */}
      <Sheet open={showDrawer} onOpenChange={setShowDrawer}>
        <SheetContent className="bg-slate-800 border-slate-700 text-white w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              {selectedNarrator?.display_name}
              {selectedNarrator?.is_verified && (
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              )}
            </SheetTitle>
          </SheetHeader>

          {selectedNarrator && (
            <div className="space-y-6 mt-6">
              {/* Status */}
              <div className="flex gap-2">
                <Badge
                  className={
                    selectedNarrator.availability_status === "AVAILABLE"
                      ? "bg-green-500/20 text-green-300"
                      : "bg-yellow-500/20 text-yellow-300"
                  }
                >
                  {selectedNarrator.availability_status}
                </Badge>
                <Badge
                  className={
                    selectedNarrator.portfolio_status === "APPROVED"
                      ? "bg-blue-500/20 text-blue-300"
                      : "bg-slate-500/20 text-slate-300"
                  }
                >
                  Portfolio: {selectedNarrator.portfolio_status}
                </Badge>
              </div>

              {/* Bio */}
              {selectedNarrator.bio && (
                <div>
                  <h4 className="text-sm text-slate-400 mb-1">Bio</h4>
                  <p className="text-white">{selectedNarrator.bio}</p>
                </div>
              )}

              {/* Languages */}
              <div>
                <h4 className="text-sm text-slate-400 mb-2">Languages</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedNarrator.languages?.map((lang) => (
                    <Badge key={lang} variant="outline" className="border-slate-600">
                      {lang}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Stats */}
              <Card className="bg-slate-700/50 border-slate-600">
                <CardContent className="p-4 grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">
                      {selectedNarrator.demos_count || 0}
                    </p>
                    <p className="text-xs text-slate-400">Demos</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">
                      {selectedNarrator.projects_count || 0}
                    </p>
                    <p className="text-xs text-slate-400">Projects</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">
                      {selectedNarrator.languages?.length || 0}
                    </p>
                    <p className="text-xs text-slate-400">Languages</p>
                  </div>
                </CardContent>
              </Card>

              {/* Demo Review */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm text-slate-300 font-medium">
                    Uploaded Demos
                  </h4>
                  <span className="text-xs text-slate-500">
                    {narratorDemos.length} total
                  </span>
                </div>

                {demosLoading ? (
                  <div className="flex items-center justify-center rounded-md border border-slate-700 bg-slate-900/40 py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
                  </div>
                ) : narratorDemos.length === 0 ? (
                  <div className="rounded-md border border-slate-700 bg-slate-900/40 p-4 text-center text-sm text-slate-400">
                    <FileAudio className="w-8 h-8 mx-auto mb-2 text-slate-500" />
                    No demo audio uploaded yet.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {narratorDemos.map((demo) => (
                      <div
                        key={demo.id}
                        className="rounded-md border border-slate-700 bg-slate-900/40 p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <FileAudio className="w-4 h-4 text-purple-300 flex-shrink-0" />
                              <h5 className="font-medium text-white truncate">
                                {demo.title}
                              </h5>
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                              <Badge className={getDemoStatusClass(demo.status)}>
                                {demo.status}
                              </Badge>
                              <span>{demo.language}</span>
                              {demo.genre_tag && <span>{demo.genre_tag}</span>}
                              {demo.tone_tag && <span>{demo.tone_tag}</span>}
                              {formatFileSize(demo.size_bytes) && (
                                <span>{formatFileSize(demo.size_bytes)}</span>
                              )}
                              {formatDate(demo.uploaded_at) && (
                                <span>Uploaded {formatDate(demo.uploaded_at)}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <audio
                          className="mt-4 w-full"
                          controls
                          preload="metadata"
                          src={adminService.getDemoStreamUrl(demo.id)}
                          data-testid={`admin-demo-audio-${demo.id}`}
                        />

                        {demo.reviewer_notes && (
                          <p className="mt-3 rounded-md bg-slate-800 p-3 text-sm text-slate-300">
                            {demo.reviewer_notes}
                          </p>
                        )}

                        <textarea
                          value={demoReviewNotes[demo.id] || ""}
                          onChange={(e) =>
                            setDemoReviewNotes((prev) => ({
                              ...prev,
                              [demo.id]: e.target.value,
                            }))
                          }
                          placeholder="Review notes for this demo..."
                          className="mt-3 min-h-20 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-purple-400 focus:outline-none"
                        />

                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <Button
                            onClick={() => handleReviewDemo(demo, "APPROVED")}
                            disabled={reviewingDemoId === demo.id}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {reviewingDemoId === demo.id ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                            )}
                            Approve Demo
                          </Button>
                          <Button
                            onClick={() => handleReviewDemo(demo, "REJECTED")}
                            disabled={reviewingDemoId === demo.id}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            {reviewingDemoId === demo.id ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <XCircle className="w-4 h-4 mr-2" />
                            )}
                            Reject Demo
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="space-y-3">
                {!selectedNarrator.is_verified && (
                  <Button
                    onClick={() => handleVerify(selectedNarrator.id)}
                    disabled={processing}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {processing ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    Verify Narrator
                  </Button>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
